import dayjs from 'dayjs'
import { useEffect, useRef } from 'react'
import { showVersionUpdateToast } from 'src/components/ui/VersionUpdateToastShadcn'
import { useFetchBuildInfoQuery } from 'src/redux/api/rest/subscriptionApi'

/**
 * Check if we're running on localhost/development environment
 */
function isLocalhost(): boolean {
	if (typeof window === 'undefined') return false

	const hostname = window.location.hostname
	return (
		hostname === 'localhost' ||
		hostname === '127.0.0.1' ||
		hostname.startsWith('192.168.') ||
		hostname.endsWith('.local') ||
		hostname === '::1' // IPv6 localhost
	)
}

/**
 * Enhanced version check hook that uses RTK Query for better error handling
 */
export function useAmplifyVersionCheck() {
	const currentVersionRef = useRef<string | null>(null)
	const hasShownNotificationRef = useRef(false)

	// Initialize current version from package.json build date
	// biome-ignore lint: Version initialization - sets current version from package.json on mount
	useEffect(() => {
		if (
			typeof window !== 'undefined' &&
			(window as any).__PACKAGE_BUILD_DATE__
		) {
			currentVersionRef.current = String(
				(window as any).__PACKAGE_BUILD_DATE__
			)
		}
	}, [])

	// Query build info every 2 minutes
	const {
		data: buildInfo,
		error,
		isError,
	} = useFetchBuildInfoQuery(undefined, {
		pollingInterval: 2 * 60 * 1000, // Poll every 2 minutes
		refetchOnFocus: true, // Refetch when tab becomes focused
	})

	// Check for version changes
	// biome-ignore lint: Version check - compares build dates and triggers reload on version mismatch
	useEffect(() => {
		if (buildInfo?.buildDate && currentVersionRef.current) {
			const serverVersion = String(buildInfo.buildDate)
			const localVersion = currentVersionRef.current

			// Parse build dates as timestamps and calculate the difference
			const serverDate = dayjs(Number(buildInfo.buildDate))
			const localDate = dayjs(Number(localVersion))
			const timeDiffInMinutes = Math.abs(
				serverDate.diff(localDate, 'minute')
			)

			if (
				serverVersion !== localVersion &&
				serverVersion !== 'unknown' &&
				!hasShownNotificationRef.current &&
				timeDiffInMinutes > 30
			) {
				console.warn('Version mismatch detected via Amplify API:', {
					local: localVersion,
					server: serverVersion,
					timeDifferenceMinutes: timeDiffInMinutes,
					branch: buildInfo.branch,
					commitId: buildInfo.commitId,
					commitMessage: buildInfo.commitMessage,
				})

				// Skip version mismatch notifications on localhost
				if (!isLocalhost()) {
					// Show toast notification
					showVersionUpdateToast(serverVersion)
				} else {
					console.debug(
						'Version mismatch detected on localhost via Amplify API - ignoring notification'
					)
				}

				// Mark as shown to prevent repeated notifications
				hasShownNotificationRef.current = true

				// Update current version reference
				currentVersionRef.current = serverVersion
			} else if (
				serverVersion !== localVersion &&
				timeDiffInMinutes <= 30
			) {
				console.debug(
					'Version mismatch detected but within 30 minute threshold:',
					{
						local: localVersion,
						server: serverVersion,
						timeDifferenceMinutes: timeDiffInMinutes,
					}
				)
			}
		}
	}, [buildInfo])

	// Log errors for debugging
	useEffect(() => {
		if (isError && error) {
			console.debug('Build info fetch failed:', error)
		}
	}, [isError, error])

	return {
		buildInfo,
		isError,
		error,
		currentVersion: currentVersionRef.current,
	}
}
